import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  ArrowRight,
  Calendar,
  Check,
  Eye,
  MapPin,
  Rocket,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
} from 'lucide-react';
import { Badge, Button, PageHeader, Spinner } from '../components/ui';
import { adminEventService, type AdminEvent } from '../services/adminEventService';
import { eventBuilderService } from '../services/eventBuilderService';
import { eventSetupSummary } from '../utils/eventWorkspace';

export default function EventOverviewPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<AdminEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [aiEditing, setAiEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) {
      setError('Event ID is missing.');
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    adminEventService.get(eventId)
      .then((loaded) => {
        if (active) setEvent(loaded);
      })
      .catch((loadError) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'Could not load event.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [eventId]);

  async function editWithAI() {
    if (!eventId || !event || event.effective_role === 'viewer') return;
    setAiEditing(true);
    setError(null);
    try {
      const draft = await eventBuilderService.editEvent(eventId);
      navigate(`/ai-event-builder/${draft.id}`);
    } catch (editError) {
      setError(editError instanceof Error ? editError.message : 'Unable to start AI editing.');
    } finally {
      setAiEditing(false);
    }
  }

  if (loading) return <div className="flex justify-center py-24"><Spinner className="h-6 w-6 text-gray-500" /></div>;
  if (!event || !eventId) return <div role="alert" className="m-8 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{error ?? 'Event not found.'}</div>;

  const summary = eventSetupSummary(event);
  const canManage = event.effective_role === 'admin' || event.effective_role === 'manager';
  const attendeeBaseUrl = import.meta.env.VITE_ATTENDEE_URL || (import.meta.env.DEV ? 'http://localhost:5173' : '');
  const steps = [
    {
      label: 'Content & schedule',
      description: 'Add or import sessions, speakers, papers, and event organizations.',
      href: `/events/${eventId}/content`,
      icon: Calendar,
    },
    {
      label: 'Attendee experience',
      description: 'Choose what attendees see and arrange the event home page.',
      href: `/events/${eventId}/app-setup`,
      icon: SlidersHorizontal,
    },
    {
      label: 'People & access',
      description: 'Configure access rules, invitations, and attendee information.',
      href: `/events/${eventId}/access`,
      icon: ShieldCheck,
    },
    {
      label: 'Review & publish',
      description: 'Preview the attendee app and make the event available.',
      href: `/events/${eventId}/publish`,
      icon: Rocket,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl p-8">
      <PageHeader
        title={event.name}
        description={canManage ? 'Follow the setup guide, or jump directly to any event control.' : 'Explore this event workspace.'}
        action={(
          <div className="flex items-center gap-2">
            {attendeeBaseUrl && (
              <a
                href={`${attendeeBaseUrl}/event-preview/${eventId}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-100 hover:bg-gray-700"
              >
                <Eye size={14} /> Preview attendee app
              </a>
            )}
            {canManage && <Button variant="secondary" icon={<Sparkles size={14} />} loading={aiEditing} onClick={() => void editWithAI()}>
              Edit with AI
            </Button>}
          </div>
        )}
      />

      {error && <p role="alert" className="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{error}</p>}

      <section className="mb-6 rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900 to-gray-900/60 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <Badge variant={event.status === 'draft' ? 'warning' : 'info'}>{event.status[0].toUpperCase() + event.status.slice(1)}</Badge>
              <span className="text-xs text-gray-500">Event workspace</span>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-300">
              <span className="inline-flex items-center gap-2"><Calendar size={15} className="text-gray-500" />{event.date_start} – {event.date_end}</span>
              <span className="inline-flex items-center gap-2"><MapPin size={15} className="text-gray-500" />{event.location}</span>
            </div>
          </div>
          {canManage && <div className="rounded-xl border border-sky-500/20 bg-sky-500/5 px-4 py-3">
            <p className="text-xs font-medium text-sky-300">Recommended next step</p>
            <Link to={`/events/${eventId}/content`} className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-white hover:text-sky-300">
              Add event content <ArrowRight size={14} />
            </Link>
          </div>}
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
        <section aria-labelledby="setup-guide-heading">
          <div className="mb-4">
            <h2 id="setup-guide-heading" className="text-base font-semibold text-white">Setup guide</h2>
            <p className="mt-1 text-sm text-gray-500">Work from top to bottom for a launch-ready event.</p>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-4 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
              <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-400"><Check size={16} /></div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold text-white">Event basics</h3>
                  <span className="text-xs font-medium text-emerald-400">{summary.basicsComplete ? 'Complete' : 'Needs attention'}</span>
                </div>
                <p className="mt-1 text-xs text-gray-400">Name, dates, timezone, and location are configured.</p>
              </div>
            </div>

            {steps.map(({ label, description, href, icon: Icon }, index) => (
              <Link key={label} to={href} className="group flex items-start gap-4 rounded-xl border border-gray-800 bg-gray-900 p-4 transition-colors hover:border-sky-500/30 hover:bg-gray-800/80">
                <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-gray-800 text-gray-400 group-hover:bg-sky-500/10 group-hover:text-sky-400"><Icon size={16} /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold text-white">{label}</h3>
                    <span className="text-xs text-gray-500">Step {index + 2}</span>
                  </div>
                  <p className="mt-1 text-xs text-gray-400">{description}</p>
                </div>
                <ArrowRight size={16} className="mt-2 text-gray-600 group-hover:text-sky-400" />
              </Link>
            ))}
          </div>
        </section>

        {canManage && <aside className="h-fit rounded-xl border border-gray-800 bg-gray-900 p-5">
          <Sparkles size={18} className="text-sky-400" />
          <h2 className="mt-3 text-sm font-semibold text-white">Need help finishing?</h2>
          <p className="mt-1 text-xs leading-5 text-gray-400">AI can update this event using the details and content already saved here. You approve changes before they are applied.</p>
          <Button className="mt-4 w-full justify-center" variant="secondary" loading={aiEditing} onClick={() => void editWithAI()}>Continue with AI</Button>
        </aside>}
      </div>
    </div>
  );
}
